import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { connect } from 'react-redux';
import { get, keyBy, isEqual, isEmpty, set } from 'lodash';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers-pro';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import {
  closeGenericModal,
  openGenericModal,
  openSmallModal,
  refreshGenericModal,
} from '@actions/modal';

import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_SAVE_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';
import { updateIsSynchronizingCashierProductsSales } from '@actions/cashierProduct';
import { receiveUser as updateUserInStore } from '@actions/user';

import { Button, ToggleSwitch } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { getLinearGradientButtonForProgressStyling } from '@commons/utils/cssTricks';
import LastAction from '@commons/LastAction';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import WhiteCard from '@commons/WhiteCard';

import { getClientInfo } from '@selectors/client';

import { CLIENT_STATUS } from '../BackOfficeGeneral/utils/constants';

import { cashierProductSalesService } from '@services/cashierProductSales';
import storeService from '@services/store';

import theme from '@theme';

import {
  Container,
  WhiteCardsContainer,
  ButtonWithLabel,
  ButtonLabel,
  TimePickerWithButton,
  ActionsContainer,
  ActionRow,
} from './styledComponents';
import { SYNC_STATE } from '../commons/syncStatus';
import ExportRangeProductSales from './component/exportRangeProductSales';
import getModalConfig, { SYNC_CONFIG_MODAL_STEPS } from './SyncModal/utils/getConfiguration';

const ACTIONS_TYPES = {
  SYNC_CASHIER_PRODUCT_SALES: 'syncCashierProductSales',
  DOWNLOAD_CASHIER_PRODUCT_SALES: 'downloadCashierProductSales',
};

const TIMEOUT_GET_SYNC_STATUS = 5000; // in ms

const LAST_FORECAST_INIT_STATE = { active: false, lastRunDate: null };

const BackOfficeSales = (props) => {
  const {
    match,
    user,
    client: {
      clientId,
      clientName,
      hasMultipleBrands,
      storeName,
      clientStatus,
      salesHistoryRetrievedAt,
    },
    openGenericModal,
    openModalExportInfo,
    refreshGenericModal,
    closeGenericModal,
    pageLoaded,
    pageLoading,
    showErrorMessage,
    showSuccessMessage,
    isSynchronizingCashierProductsSales,
    updateIsSynchronizingCashierProductsSales,
    updateUserInStore,
  } = props;

  const path = match.path || null;

  /*********************************/
  /** PAGE DATA **/
  /*********************************/
  const [initSync, setInitSync] = useState(false);

  const [lastSyncDate, setLastSyncDate] = useState(null);
  const [syncSchedule, setSyncSchedule] = useState(null);
  const [syncScheduleTimePicker, setSyncScheduleTimePicker] = useState(null);
  const [ongoingSyncInfo, setOngoingSyncInfo] = useState({});
  const [lastForecast, setLastForecast] = useState(LAST_FORECAST_INIT_STATE);
  const [forecastSchedule, setForecastSchedule] = useState(null);
  const [forecastScheduleTimePicker, setForecastScheduleTimePicker] = useState(null);
  /*********************************/
  /** MODAL DATA **/
  /*********************************/
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncLoading, setIsSyncLoading] = useState(true);
  const [isForecastLoading, setIsForecastLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [actionType, setActionType] = useState(null);
  const [salesDateRange, setSalesDateRange] = useState();

  const handleStoresSelection = (storesToSelect) => {
    const selectableStores = storesToSelect.filter((store) => !store.isNotSelectable);

    handleSelectedStoresChange(selectableStores);
    setSelectedStores(selectableStores);
  };

  const handleSelectedStoresChange = (storesToSelect) => {
    const selectedStoreByIds = keyBy(storesToSelect, 'id');

    const formattedStores = stores.map((store) => {
      if (selectedStoreByIds[store.id]) {
        return { ...store, isRowSelected: true };
      }
      return { ...store, isRowSelected: false };
    });

    if (!isEqual(stores, formattedStores)) {
      setStores(formattedStores);
    }
  };

  const getLastIngestion = async () => {
    try {
      const { lastRunDate, schedule } = await cashierProductSalesService.getIngestionPipelineStatus(
        clientId,
      );

      const formattedSyncSchedule = schedule
        ? moment(schedule, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
        : null;

      setLastSyncDate(lastRunDate || null);
      setSyncSchedule(formattedSyncSchedule);
      setSyncScheduleTimePicker(formattedSyncSchedule);
    } catch (error) {
      setLastSyncDate(null);
      setSyncSchedule(null);
      setSyncScheduleTimePicker(null);
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SYNC_STATUS_ERROR'));
    } finally {
      setIsSyncLoading(false);
    }
  };

  const getLastForecast = async (clientId) => {
    try {
      const { lastRunDate, active, schedule } =
        await cashierProductSalesService.getForecastPipelineStatus(clientId);

      const formattedForecastSchedule = schedule
        ? moment(schedule, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
        : null;
      setLastForecast({ active: active, lastRunDate: lastRunDate });
      setForecastSchedule(formattedForecastSchedule);
      setForecastScheduleTimePicker(formattedForecastSchedule);
    } catch (error) {
      setLastForecast(LAST_FORECAST_INIT_STATE);
      setForecastSchedule(null);
      setForecastScheduleTimePicker(null);
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SYNC_STATUS_ERROR'));
    } finally {
      setIsForecastLoading(false);
    }
  };

  const setModalFirstStep = async (actionType) => {
    setIsLoading(true);
    setActionType(actionType);
    setCurrentStep(SYNC_CONFIG_MODAL_STEPS.STORE_SELECTION);
    const clientStores = await fetchClientStore();
    setStores(clientStores);
    setIsLoading(false);
  };

  const resetData = async () => {
    setSelectedStores([]);
    setStores([]);
    setCurrentStep(null);
    setActionType(null);
    setSalesDateRange(null);
  };

  const syncCashierProductSales = async (
    selectedStoresIds,
    formattedStartDate,
    formattedEndDate,
  ) => {
    pageLoading();
    closeGenericModal();

    try {
      const { progress, estimatedEndDate } =
        await cashierProductSalesService.syncCashierProductSales(
          selectedStoresIds,
          formattedStartDate,
          formattedEndDate,
        );
      showSuccessMessage(i18next.t('BACKOFFICE.DATA.SYNC_START_SUCCESS'));
      updateIsSynchronizingCashierProductsSales(true);
      setOngoingSyncInfo({ progress, estimatedEndDate });
    } catch (error) {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SYNC_START_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const exportCashierProductSales = async (
    formattedStartDate,
    formattedEndDate,
    selectedStoresIds,
  ) => {
    try {
      closeGenericModal(); // This is here and not outside of the if because i want to close the modal before calling the API

      const params = {
        component: ExportRangeProductSales,
        clientName,
        storeName,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        selectedStoresIds,
      };

      openModalExportInfo(params);
    } catch {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.DOWNLOAD_CASHIER_PRODUCT_SALES_ERROR'));
    }
  };

  const handleValidation = async () => {
    const selectedStoresIds = selectedStores.map((store) => store.id);
    const formattedStartDate = salesDateRange.startDate
      ? salesDateRange.startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
      : null;
    const formattedEndDate = salesDateRange.endDate
      ? salesDateRange.endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY)
      : null;

    /* Since we close the modal at validation (when we call the api), it is technically possible to re-open the modal while the api call is running
     *  As we do not want the data from the previous action to remain, we reset it now.
     */
    resetData();

    if (actionType === ACTIONS_TYPES.SYNC_CASHIER_PRODUCT_SALES) {
      await syncCashierProductSales(selectedStoresIds, formattedStartDate, formattedEndDate);
      return;
    }

    await exportCashierProductSales(formattedStartDate, formattedEndDate, selectedStoresIds);
  };

  const handleRetrieveSalesHistory = async () => {
    pageLoading();

    try {
      const { salesHistoryRetrievedAt: retrievedAt } =
        await cashierProductSalesService.retrieveSalesHistory(clientId);

      const updatedUserInfo = set(
        { ...user },
        'lnkClientAccountrel.salesHistoryRetrievedAt',
        retrievedAt,
      );

      updateUserInStore(updatedUserInfo);

      showSuccessMessage(i18next.t('BACKOFFICE.DATA.RETRIEVE_SALES_HISTORY_SUCCESS'));
    } catch (error) {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.RETRIEVE_SALES_HISTORY_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const openRetrieveSalesHistoryModal = () => {
    const modalConfig = getConfirmationModal({
      title: i18next.t('BACKOFFICE.DATA.RETRIEVE_SALES_HISTORY_MODAL_TITLE'),
      icon: '/images/inpulse/new-price-white-small.svg',
      content: i18next.t('BACKOFFICE.DATA.RETRIEVE_SALES_HISTORY_MODAL_CONTENT'),
      actions: [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          ...GENERIC_MODAL_SAVE_BUTTON(),
          handleClick: handleRetrieveSalesHistory,
        },
      ],
    });

    openGenericModal(modalConfig);
  };

  const handleSaveSyncSchedule = async (selectedSyncSchedule) => {
    try {
      await cashierProductSalesService.setIngestionPipelineSchedule(
        selectedSyncSchedule.format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE),
      );

      showSuccessMessage(i18next.t('BACKOFFICE.DATA.SET_SALES_SYNC_TIME_SUCCESS'));
      setSyncSchedule(selectedSyncSchedule);
    } catch (error) {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SET_SALES_SYNC_TIME_ERROR'));
    }
  };

  const handleSaveForecastSchedule = async (selectedForecastSchedule) => {
    try {
      await cashierProductSalesService.setForecastPipelineSchedule(
        selectedForecastSchedule.format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE),
      );

      showSuccessMessage(i18next.t('BACKOFFICE.DATA.SET_FORECAST_TIME_SUCCESS'));
      setForecastSchedule(selectedForecastSchedule);
    } catch (error) {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SET_FORECAST_TIME_ERROR'));
    }
  };

  const fetchClientStore = async () => {
    try {
      const stores = await storeService.getStoresOfClient();

      const activeStores = stores.filter((store) => store.active);

      return activeStores.map((store) => ({
        ...store,
        ...(hasMultipleBrands ? { brandName: get(store, 'lnkBrandStorerel.name') || '' } : {}),
        mappedAccounts: get(store, 'mappedAccounts.length'),
        isRowSelected: false,
        isNotSelectable:
          !get(store, 'mappedCashierStores.length') ||
          get(store, 'mappedCashierStores.length') === 0,
      }));
    } catch (error) {
      props.showErrorMessage(i18next.t('BACKOFFICE.TEAM.ASSOCIATE_STORES_FETCH_ERROR'));
      return [];
    }
  };

  useEffect(() => {
    pageLoading();
    if (!user) {
      return;
    }

    (async function loadData() {
      await Promise.all([getLastIngestion(), getLastForecast(clientId)]);
      pageLoaded();
    })();
  }, []);

  const checkSyncStatus = async () => {
    try {
      const { state, progress, estimatedEndDate } =
        await cashierProductSalesService.getCashierProductsSalesSyncStatus();

      if ([SYNC_STATE.RETRY, SYNC_STATE.PENDING, SYNC_STATE.STARTED].includes(state)) {
        updateIsSynchronizingCashierProductsSales(true);

        setOngoingSyncInfo({ progress, estimatedEndDate });

        return true;
      }

      if (!!isSynchronizingCashierProductsSales) {
        if (state === SYNC_STATE.SUCCESS) {
          showSuccessMessage(i18next.t('BACKOFFICE.DATA.SYNC_SUCCESS'));
          updateIsSynchronizingCashierProductsSales(false);

          setOngoingSyncInfo({});

          return false;
        }

        if (state === SYNC_STATE.FAILURE || state === SYNC_STATE.REVOKED) {
          showErrorMessage(i18next.t('BACKOFFICE.DATA.SYNC_ERROR'));

          setOngoingSyncInfo({});
        }
      }
    } catch (error) {
      showErrorMessage(i18next.t('BACKOFFICE.DATA.SYNC_STATUS_ERROR'));
    }

    updateIsSynchronizingCashierProductsSales(false);

    setOngoingSyncInfo({});

    return false;
  };

  useEffect(() => {
    let intervalSync;

    if (isSynchronizingCashierProductsSales) {
      intervalSync = setInterval(() => {
        checkSyncStatus();
      }, TIMEOUT_GET_SYNC_STATUS);
    }

    (async function fetchSyncStatus() {
      const sync = await checkSyncStatus();

      setInitSync(sync);
    })();

    return () => clearInterval(intervalSync);
  }, [isSynchronizingCashierProductsSales]);

  useEffect(() => {
    const storesToSelectById = keyBy(selectedStores, 'id');

    const formattedStores = stores.map((store) => {
      if (storesToSelectById[store.id]) {
        return { ...store, isRowSelected: true };
      }
      return { ...store };
    });

    setStores(formattedStores);
  }, [currentStep]);

  useEffect(() => {
    if (!currentStep) {
      return;
    }

    const isModalOpened = get(props, 'modal.GenericModalBool', false);

    const modalConfiguration = getModalConfig({
      currentStep,
      user,
      stores,
      selectedStores,
      salesDateRange,
      isLoading,
      handleSelectedStoresChange,
      setCurrentStep,
      handleStoresSelection,
      resetData,
      handleValidation,
      setSalesDateRange,
    });

    if (isModalOpened) {
      refreshGenericModal(modalConfiguration);
      return;
    }

    openGenericModal(modalConfiguration);
  }, [currentStep, selectedStores, salesDateRange, isLoading]);

  const shouldDisplayCountDown = !isEmpty(ongoingSyncInfo);

  return (
    <Container>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <NavigationBreadCrumb featurePath={path} />
        <WhiteCardsContainer>
          <WhiteCard
            buttonFlexColumn={false}
            buttonFlexDirection="row"
            content={i18next.t('BACKOFFICE.DATA.SYNC_SALES_DATA_CONTENT')}
            contentColor={theme.colors.greys.darker}
            renderContent={
              <ActionsContainer>
                <ActionRow>
                  <Button
                    animation={{ rotate: initSync }}
                    buttonCustomStyle={{ width: 'fit-content' }}
                    customBackground={getLinearGradientButtonForProgressStyling(
                      ongoingSyncInfo.progress,
                    )}
                    handleClick={() => {
                      setModalFirstStep(ACTIONS_TYPES.SYNC_CASHIER_PRODUCT_SALES);
                    }}
                    icon={'/images/inpulse/sync-white-small.svg'}
                    isDisabled={initSync || isSyncLoading}
                    label={i18next.t('GENERAL.SYNC')}
                  />
                  <LastAction
                    date={shouldDisplayCountDown ? ongoingSyncInfo.estimatedEndDate : lastSyncDate}
                    displayCountDown={shouldDisplayCountDown}
                    label={i18next.t(
                      shouldDisplayCountDown ? 'GENERAL.TIME_LEFT' : 'GENERAL.LAST_SYNC',
                    )}
                  />
                </ActionRow>
                <ActionRow>
                  <Button
                    buttonCustomStyle={{ width: 'fit-content' }}
                    handleClick={openRetrieveSalesHistoryModal}
                    icon={'/images/inpulse/new-price-white-small.svg'}
                    isDisabled={
                      !!salesHistoryRetrievedAt ||
                      clientStatus !== CLIENT_STATUS.RUN.toLocaleLowerCase()
                    }
                    label={i18next.t('BACKOFFICE.DATA.RETRIEVE_SALES_HISTORY_ACTION')}
                  />
                  <LastAction
                    date={salesHistoryRetrievedAt}
                    dateFormat={'DD/MM/YYYY à HH:mm'}
                    label={i18next.t('BACKOFFICE.DATA.SALES_HISTORY_RETRIEVED_AT')}
                  />
                </ActionRow>
              </ActionsContainer>
            }
            title={i18next.t('BACKOFFICE.DATA.SYNC_SALES_DATA_TITLE')}
          />
          <WhiteCard
            buttonFlexColumn={false}
            buttonFlexDirection="row"
            contentColor={theme.colors.greys.darker}
            renderContent={
              <TimePickerWithButton>
                <TimePicker
                  disabled={isSyncLoading}
                  value={syncScheduleTimePicker}
                  onChange={setSyncScheduleTimePicker}
                />
                <Button
                  handleClick={() => handleSaveSyncSchedule(syncScheduleTimePicker)}
                  icon={'/images/inpulse/save-white-small.svg'}
                  isDisabled={
                    isSyncLoading || !syncSchedule || isEqual(syncSchedule, syncScheduleTimePicker)
                  }
                  label={i18next.t('GENERAL.SAVE')}
                />
              </TimePickerWithButton>
            }
            title={i18next.t('BACKOFFICE.DATA.SET_SALES_SYNC_TIME')}
          />
          <WhiteCard
            renderContent={
              <ButtonWithLabel>
                <ToggleSwitch
                  checked={lastForecast.active}
                  handleClick={() => {}}
                  id={'forecast-toggle'}
                  disabled
                />
                <ButtonLabel>
                  {i18next.t(
                    lastForecast.active
                      ? 'BACKOFFICE.DATA.FORECAST_STATUS_ENABLED'
                      : 'BACKOFFICE.DATA.FORECAST_STATUS_DISABLED',
                  )}
                </ButtonLabel>
              </ButtonWithLabel>
            }
            renderLastAction={
              <LastAction date={lastForecast.lastRunDate} label={i18next.t('GENERAL.LAST_EXEC')} />
            }
            title={i18next.t('BACKOFFICE.DATA.FORECAST_TITLE')}
          />
          <WhiteCard
            buttonFlexColumn={false}
            buttonFlexDirection="row"
            contentColor={theme.colors.greys.darker}
            renderContent={
              <TimePickerWithButton>
                <TimePicker
                  disabled={isForecastLoading}
                  value={forecastScheduleTimePicker}
                  onChange={setForecastScheduleTimePicker}
                />
                <Button
                  handleClick={() => handleSaveForecastSchedule(forecastScheduleTimePicker)}
                  icon={'/images/inpulse/save-white-small.svg'}
                  isDisabled={
                    isForecastLoading ||
                    !forecastSchedule ||
                    isEqual(forecastSchedule, forecastScheduleTimePicker)
                  }
                  label={i18next.t('GENERAL.SAVE')}
                />
              </TimePickerWithButton>
            }
            title={i18next.t('BACKOFFICE.DATA.SET_FORECAST_TIME')}
          />
        </WhiteCardsContainer>
      </LocalizationProvider>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  modal: state.modalReducer,
  isSynchronizingCashierProductsSales: state.baseReducer.isSynchronizingCashierProductsSales,
  client: getClientInfo(state.baseReducer.user),
});

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openModalExportInfo: (params) => {
    dispatch(openSmallModal(params));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  closeGenericModal: (params) => {
    dispatch(closeGenericModal(params));
  },
  updateIsSynchronizingCashierProductsSales: (isSynchronizingCashierProductsSales) => {
    dispatch(updateIsSynchronizingCashierProductsSales(isSynchronizingCashierProductsSales));
  },
  updateUserInStore: (updatedUser) => {
    dispatch(updateUserInStore(updatedUser));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(BackOfficeSales);
